"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, Phone, Bookmark, Share2, Ellipsis as MoreHorizontal } from 'lucide-react';
export default function TheExpertTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const reviews = [{
    reviewer_name: "Pablo Huertas",
    rating: 5,
    review_text: "Ignacio with Arbor Pros was amazing. His team did a great job of cutting down my Bradford Pear. The clean up was also great. They are highly recommended!",
    timestamp: "2 months ago",
    avatar_initial: "P",
    review_count: "8 reviews",
    color: "bg-orange-600"
  }, {
    reviewer_name: "Ryan McAfee",
    rating: 5,
    review_text: "Ignacio has done excellent work every time I've contracted him. Trees overgrown? No problem - he trims the limbs and protects the house.",
    timestamp: "6 months ago",
    avatar_initial: "R",
    review_count: "12 reviews",
    color: "bg-purple-700"
  }, {
    reviewer_name: "Preston Caldwell",
    rating: 5,
    review_text: "Arbor Pro was super professional and easy to work with from start to finish. Communication was quick, they showed up on time, and the crew did a...",
    timestamp: "7 months ago",
    avatar_initial: "P",
    review_count: "1 review",
    color: "bg-indigo-600"
  }, {
    reviewer_name: "Daniel Avondet",
    rating: 5,
    review_text: "I'm so happy with the service I received from Arbor Pro tree service. Ignacio pruned a pecan tree in front and eliminated branches from trees from a...",
    timestamp: "10 months ago",
    avatar_initial: "D",
    review_count: "7 reviews",
    color: "bg-blue-800"
  }];
  return <section data-section-id="4000" id="customer-reviews" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={ref} className={`max-w-[1200px] mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-secondary">
              01 // CUSTOMER REVIEWS
            </span>
          </div>

          <h2 className="font-default font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tighter uppercase mb-8">
            TRUSTED BY<br />
            DFW HOMEOWNERS<span className="text-accent">.</span>
          </h2>

          <div className="max-w-2xl">
            <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/90">
              Real feedback from your neighbors in Fort Worth and the surrounding Metroplex. 
              We take pride in every cut and every cleanup.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 max-w-4xl mx-auto">
          {reviews.map((review, i) => <Card key={i} data-index={i} className="rounded-lg shadow-sm bg-slate-50 border-gray-200 overflow-hidden">
              <CardContent className="p-6">
                {}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <SafeHydrate><Avatar className={`h-12 w-12 rounded-lg ${review.color} flex items-center justify-center text-white font-bold text-xl`}>
                      <AvatarFallback className="bg-transparent text-white">
                        {review.avatar_initial}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <h3 className="font-bold text-base text-foreground leading-none mb-1">
                        {review.reviewer_name || "Anonymous"}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-transparent p-0 text-xs text-muted-foreground font-normal hover:bg-transparent">
                          {review.review_count || "1 review"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="text-muted-foreground rounded-lg">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </div>

                {}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, index) => <Star key={index} className={`h-4 w-4 ${index < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />)}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {review.timestamp}
                  </span>
                </div>

                {}
                <div className="mb-6">
                  <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
                    {review.review_text || "No review provided"}
                    {review.review_text.length > 120 && <Button variant="link" className="h-auto p-0 ml-1 text-muted-foreground font-medium hover:no-underline">
                        ...more
                      </Button>}
                  </p>
                </div>

                {}
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
                  <Button variant="outline" className="rounded-lg bg-sky-50 border-sky-100 text-sky-700 hover:bg-sky-100 h-9 px-4 gap-2">
                    <Phone className="h-4 w-4" />
                    Call
                  </Button>
                  <Button variant="outline" className="rounded-lg bg-sky-50 border-sky-100 text-sky-700 hover:bg-sky-100 h-9 px-4 gap-2">
                    <Bookmark className="h-4 w-4" />
                    Save
                  </Button>
                  <Button variant="outline" className="rounded-lg bg-sky-50 border-sky-100 text-sky-700 hover:bg-sky-100 h-9 px-4 gap-2">
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div className="mt-24 md:mt-32 pt-10 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-widest text-secondary">
            § ARBOR PRO — GOOGLE VERIFIED REVIEWS
          </p>
          <p className="font-mono text-xs uppercase tracking-widest text-secondary">
            4.9 AVG RATING / 250+ REVIEWS / DFW AREA
          </p>
        </div>
      </div>
    </section>;
}